/**
 * Notifications push de cet appareil (décision 1 du 19/10/2026) : Web Push par
 * le service worker de la PWA (`public/sw-push.js`), sans application de store.
 *
 * - La personne active le push dans « Mes notifications » : le navigateur demande
 *   son accord, s'abonne avec la clé publique de MyEquip, et l'API enregistre
 *   l'abonnement pour CE compte et CETTE session.
 * - Un navigateur n'a qu'un abonnement : il appartient à la dernière personne qui
 *   l'a activé (repère local `myequip.push.owner`, sans aucun secret). Si quelqu'un
 *   d'autre se connecte, l'abonnement est rendu : jamais les alertes d'un collègue
 *   sur ce téléphone.
 * - À la déconnexion, l'API oublie l'appareil et le navigateur rend l'abonnement ;
 *   la même personne, reconnectée, le retrouve sans nouvelle question.
 */
import { api } from './api.js';

export type PushState =
  | 'indisponible' | 'non_pris_en_charge' | 'ios_a_installer' | 'bloque' | 'inactif' | 'actif';

export interface PushStatus {
  available: boolean;
  publicKey: string | null;
}

const OWNER_KEY = 'myequip.push.owner';

function readOwner(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}

function writeOwner(userId: string | null): void {
  try {
    if (userId) localStorage.setItem(OWNER_KEY, userId);
    else localStorage.removeItem(OWNER_KEY);
  } catch {
    /* sans stockage, l'abonnement est rendu à la prochaine ouverture : jamais gardé à tort */
  }
}

/** iPhone et iPad (iPadOS se présente comme un Mac tactile). */
function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

/** Ce navigateur peut-il recevoir des notifications push ? */
export function pushSupport(): 'ok' | 'non_pris_en_charge' | 'ios_a_installer' {
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) return 'ok';
  // iOS : Web Push seulement dans l'application ajoutée à l'écran d'accueil (iOS 16.4+).
  return isIos() ? 'ios_a_installer' : 'non_pris_en_charge';
}

/** Clé publique VAPID (base64url) → octets attendus par `pushManager.subscribe`. */
export function applicationServerKey(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  const raw = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

/** Libellé court de l'appareil, pour s'y retrouver : « Android · Chrome ». */
export function deviceLabel(userAgent: string): string {
  const system = /Android/.test(userAgent) ? 'Android'
    : /iPhone|iPad|iPod/.test(userAgent) ? 'iPhone ou iPad'
      : /Windows/.test(userAgent) ? 'Windows'
        : /Mac OS X|Macintosh/.test(userAgent) ? 'Mac'
          : /Linux/.test(userAgent) ? 'Linux' : 'Appareil';
  const browser = /Edg\//.test(userAgent) ? 'Edge'
    : /SamsungBrowser/.test(userAgent) ? 'Samsung Internet'
      : /Firefox\//.test(userAgent) ? 'Firefox'
        : /Chrome\//.test(userAgent) ? 'Chrome'
          : /Safari\//.test(userAgent) ? 'Safari' : 'navigateur';
  return `${system} · ${browser}`;
}

/** Le service worker de l'application (absent du serveur de développement). */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (pushSupport() !== 'ok') return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

function sameKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === key.length && bytes.every((byte, index) => byte === key[index]);
}

/** Abonnement de ce navigateur pour la clé en cours (une clé changée impose un nouvel abonnement). */
async function subscribe(worker: ServiceWorkerRegistration, publicKey: string): Promise<PushSubscription> {
  const key = applicationServerKey(publicKey);
  const existing = await worker.pushManager.getSubscription();
  if (existing && sameKey(existing, key)) return existing;
  if (existing) await existing.unsubscribe();
  return worker.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
}

async function register(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await api('/notifications/push/subscription', {
    method: 'PUT',
    body: {
      endpoint: subscription.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
      label: deviceLabel(navigator.userAgent),
    },
  });
}

/** État affiché dans « Mes notifications ». */
export async function pushState(status: PushStatus, userId: string): Promise<PushState> {
  if (!status.available) return 'indisponible';
  const support = pushSupport();
  if (support !== 'ok') return support;
  if (Notification.permission === 'denied') return 'bloque';
  const worker = await registration();
  if (!worker) return 'non_pris_en_charge';
  const subscription = await worker.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' && readOwner() === userId ? 'actif' : 'inactif';
}

/** Activation, après le geste de la personne : accord du navigateur, abonnement, enregistrement. */
export async function enablePush(status: PushStatus, userId: string): Promise<PushState> {
  if (!status.available || !status.publicKey) return 'indisponible';
  const support = pushSupport();
  if (support !== 'ok') return support;
  const worker = await registration();
  if (!worker) return 'non_pris_en_charge';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'bloque' : 'inactif';
  const subscription = await subscribe(worker, status.publicKey);
  await register(subscription);
  writeOwner(userId);
  return 'actif';
}

/** Désactivation sur cet appareil : l'API l'oublie, le navigateur rend son abonnement. */
export async function disablePush(): Promise<void> {
  const subscription = await (await registration())?.pushManager.getSubscription();
  writeOwner(null);
  if (!subscription) return;
  try {
    await api('/notifications/push/unsubscribe', { method: 'POST', body: { endpoint: subscription.endpoint } });
  } finally {
    await subscription.unsubscribe().catch(() => false);
  }
}

/**
 * Déconnexion : même effet que la désactivation, mais la personne reste celle qui
 * a choisi le push ici. Hors ligne, le navigateur rend quand même l'abonnement :
 * plus rien n'arrive sur cet appareil.
 */
export async function releasePushOnLogout(): Promise<void> {
  const subscription = await (await registration().catch(() => null))?.pushManager.getSubscription().catch(() => null);
  if (!subscription) return;
  // Toutes les entreprises du compte : la déconnexion ferme leurs sessions sur cet appareil.
  await api('/notifications/push/unsubscribe', { method: 'POST', body: { endpoint: subscription.endpoint, everywhere: true } })
    .catch(() => undefined);
  await subscription.unsubscribe().catch(() => false);
}

/**
 * À l'ouverture d'une session : l'abonnement de ce navigateur est rattaché à la
 * session en cours (ou recréé, accord déjà donné) si la personne avait activé le
 * push ici ; celui de quelqu'un d'autre est rendu. Jamais de question posée ici.
 * `sameAccount` : les adhésions du même compte personnel (autres entreprises de
 * la même personne) gardent l'abonnement et y sont rattachées à leur tour : la
 * personne reçoit les alertes de chacune, l'entreprise quittée comprise.
 */
export async function syncPush(userId: string, sameAccount: readonly string[] = []): Promise<void> {
  const worker = await registration();
  if (!worker) return;
  const existing = await worker.pushManager.getSubscription();
  const owner = readOwner();
  if (owner !== userId && !(owner && sameAccount.includes(owner))) {
    await existing?.unsubscribe().catch(() => false);
    return;
  }
  if (owner !== userId) writeOwner(userId);
  if (Notification.permission !== 'granted') return;
  const status = await api<PushStatus>('/notifications/push');
  if (!status.available || !status.publicKey) return;
  await register(await subscribe(worker, status.publicKey));
}

/** Message du service worker : une notification touchée demande d'ouvrir une page. */
export const OPEN_FROM_PUSH = 'MYEQUIP_OPEN_FROM_PUSH';

/**
 * Données d'ouverture portées par une notification (`sw-push.js`), validées :
 * jamais une forme inattendue transmise au routage.
 */
export function pushedNotification(raw: string | null): {
  id: string; event: string; title: string; body: string | null; ref_kind: string | null; ref_id: string | null;
  critical: boolean; read_at: string | null; created_at: string;
  /** Adhésion destinataire (entreprise du compte) : l'ouverture y bascule d'abord si ce n'est pas l'actuelle. */
  member: string | null;
} | null {
  if (!raw || raw.length > 4000) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;
  const text = (key: string, max: number): string | null =>
    typeof data[key] === 'string' && (data[key] as string).length <= max ? data[key] as string : null;
  const event = text('event', 80);
  if (!event || !/^[a-z_]+$/.test(event)) return null;
  const id = text('id', 64);
  return {
    id: id ?? '',
    event,
    title: '',
    body: text('body', 3000),
    ref_kind: text('ref_kind', 80),
    ref_id: text('ref_id', 64),
    critical: false,
    // Sans identifiant d'avis (centre coupé pour cet événement), rien à marquer lu.
    read_at: id ? null : new Date(0).toISOString(),
    created_at: '',
    member: text('user_id', 64),
  };
}
